// Terminerfassungs-Block (Korrespondenz-Modal + eigenes Terminerfassungs-Modal).
//
// Volle Replik des externen PX-Connect-Terminerfassungsformulars. Überträgt NICHTS an PX Connect
// und hat KEINE Verbindung zu den echten `Termin`-Entities. Der Zustand liegt beim Parent
// (controlled): beide Einstiege — CorrespondenceModal und PxConnectTerminModal — lesen/schreiben
// denselben `pxConnectTermine`-Datensatz in `Projekt.metadata` (siehe ADR 0004/0005).
import React from 'react';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Input,
    SimpleGrid,
    GridItem,
    Textarea,
} from '@chakra-ui/react';
import { AiOutlinePlus, AiOutlineDelete, AiOutlineCheck } from 'react-icons/ai';
import {
    pxConnectTerminFields,
    emptyPxConnectOrtstermin,
    PxConnectOrtstermin,
    validatePxConnectTermine,
} from '~/pages/projects/meta/data/pxConnect';
import {
    toDateInputValue,
    type PxConnectTerminePersistedState,
} from '~/pages/projects/meta/data/pxConnectTermine';

/** Höchstens zwei zusätzliche Ortstermine erlaubt (Vorgabe externes PX-Connect-Formular). */
const MAX_ORTSTERMINE = 2;

export type PxConnectTerminBlockProps = {
    isDisabled?: boolean;
    values: Record<string, string>;
    ortstermine: PxConnectOrtstermin[];
    onValuesChange: (values: Record<string, string>) => void;
    onOrtstermineChange: (ortstermine: PxConnectOrtstermin[]) => void;
    persisted?: PxConnectTerminePersistedState;
};

const PersistedFormLabel = ({
    children,
    htmlFor,
    isPersisted,
}: {
    children: React.ReactNode;
    htmlFor: string;
    isPersisted?: boolean;
}) => (
    <FormLabel htmlFor={htmlFor}>
        <HStack as="span" spacing={1}>
            <span>{children}</span>
            {isPersisted && (
                <Icon
                    as={AiOutlineCheck}
                    color="green.500"
                    data-test-id={`persisted-${htmlFor}`}
                    aria-label="Bereits gespeichert"
                />
            )}
        </HStack>
    </FormLabel>
);

export const PxConnectTerminBlock = ({
    isDisabled = false,
    values,
    ortstermine,
    onValuesChange,
    onOrtstermineChange,
    persisted,
}: PxConnectTerminBlockProps) => {
    const fieldErrors = validatePxConnectTermine(values);

    const setValue = (name: string, value: string) =>
        onValuesChange({ ...values, [name]: value });

    const updateOrtstermin = (index: number, key: keyof PxConnectOrtstermin, value: string) =>
        onOrtstermineChange(
            ortstermine.map((row, i) => (i === index ? { ...row, [key]: value } : row))
        );

    const addOrtstermin = () => {
        if (ortstermine.length >= MAX_ORTSTERMINE) return;
        onOrtstermineChange([...ortstermine, { ...emptyPxConnectOrtstermin }]);
    };

    const removeOrtstermin = (index: number) =>
        onOrtstermineChange(ortstermine.filter((_, i) => i !== index));

    return (
        <Box mb={6} data-test-id="pxc-termin-block">
            <SimpleGrid spacing={4} columns={2}>
                {pxConnectTerminFields.map((field) => (
                    <React.Fragment key={field.name}>
                        <GridItem colSpan={1}>
                            <FormControl isInvalid={Boolean(fieldErrors[field.name])}>
                                <PersistedFormLabel
                                    htmlFor={`pxc-${field.name}`}
                                    isPersisted={persisted?.values[field.name]}
                                >
                                    {field.label}
                                </PersistedFormLabel>
                                <Input
                                    id={`pxc-${field.name}`}
                                    data-test-id={`field-pxc-${field.name}`}
                                    type={field.type === 'date' ? 'date' : 'text'}
                                    // Automatisch erfasste Termine tragen einen vollen
                                    // ISO-Zeitstempel; ein Datumsfeld würde den verwerfen und
                                    // leer aussehen (UCPWO-387).
                                    value={
                                        field.type === 'date'
                                            ? toDateInputValue(values[field.name])
                                            : values[field.name] || ''
                                    }
                                    isDisabled={isDisabled}
                                    onChange={(e) => setValue(field.name, e.target.value)}
                                />
                                {fieldErrors[field.name] && (
                                    <FormErrorMessage
                                        data-test-id={`error-pxc-${field.name}`}
                                    >
                                        {fieldErrors[field.name]}
                                    </FormErrorMessage>
                                )}
                            </FormControl>
                        </GridItem>
                        {/* Füllzelle neben den allein stehenden Feldern; neben „Schaden
                            abgeschlossen" sitzt der Button zum Hinzufügen weiterer Ortstermine */}
                        {field.standalone && (
                            <GridItem colSpan={1}>
                                {field.name === 'schadenAbgeschlossen' && (
                                    <Flex h="100%" w="100%" align="flex-end">
                                        <Button
                                            leftIcon={<AiOutlinePlus />}
                                            variant="outline"
                                            w="100%"
                                            isDisabled={
                                                isDisabled ||
                                                ortstermine.length >= MAX_ORTSTERMINE
                                            }
                                            onClick={addOrtstermin}
                                            data-test-id="pxc-ortstermin-add"
                                        >
                                            Ortstermin hinzufügen
                                        </Button>
                                    </Flex>
                                )}
                            </GridItem>
                        )}
                    </React.Fragment>
                ))}
            </SimpleGrid>

            {/* Field-Array: weitere Ortstermine (Datum + Kommentar), via Button hinzugefügt */}
            {ortstermine.map((row, index) => (
                <SimpleGrid
                    key={index}
                    columns={2}
                    spacing={4}
                    mt={index === 0 ? 4 : 0}
                    mb={3}
                >
                    <FormControl>
                        <PersistedFormLabel
                            htmlFor={`pxc-ortstermin-${index}-datum`}
                            isPersisted={persisted?.ortstermine[index]?.datum}
                        >
                            {`Weiterer Ortstermin (${index + 1})`}
                        </PersistedFormLabel>
                        <Input
                            id={`pxc-ortstermin-${index}-datum`}
                            data-test-id={`field-pxc-ortstermin-${index}-datum`}
                            type="date"
                            value={row.datum}
                            isDisabled={isDisabled}
                            onChange={(e) => updateOrtstermin(index, 'datum', e.target.value)}
                        />
                    </FormControl>
                    <FormControl>
                        <PersistedFormLabel
                            htmlFor={`pxc-ortstermin-${index}-kommentar`}
                            isPersisted={persisted?.ortstermine[index]?.kommentar}
                        >
                            Kommentar
                        </PersistedFormLabel>
                        {/* alignItems="flex-start": der Lösch-Button bleibt an der
                            Oberkante der Textarea und wandert beim Aufziehen nicht mit */}
                        <HStack spacing={2} alignItems="flex-start">
                            <Textarea
                                id={`pxc-ortstermin-${index}-kommentar`}
                                data-test-id={`field-pxc-ortstermin-${index}-kommentar`}
                                value={row.kommentar}
                                isDisabled={isDisabled}
                                rows={1}
                                minH={10}
                                resize="vertical"
                                onChange={(e) =>
                                    updateOrtstermin(index, 'kommentar', e.target.value)
                                }
                            />
                            <IconButton
                                aria-label="Ortstermin entfernen"
                                icon={<AiOutlineDelete />}
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={isDisabled}
                                onClick={() => removeOrtstermin(index)}
                                data-test-id={`pxc-ortstermin-${index}-remove`}
                            />
                        </HStack>
                    </FormControl>
                </SimpleGrid>
            ))}
        </Box>
    );
};
